import type { TypingEvent } from "./types.js";

const TYPING_EXPIRY_MS = 5000;
// Human typing and an older daemon's frames carry no turnId: one untagged turn per sender.
const UNTAGGED = "";

interface LiveTurn {
  timer?: ReturnType<typeof setTimeout>;
  parentId?: string;
}

// One "<name> is typing…" line per sender, shown while ANY of its turns is live. Each turn
// expires 5 s after its own last frame; a stop ends only the turn it names.
export class TypingLines {
  typing: TypingEvent[] = $state([]);
  #turns = new Map<string, Map<string, LiveTurn>>();
  // ponytail: instances are app-lifetime singletons (channel, DM, thread), so never removed.
  static #live = new Set<TypingLines>();
  static #connected = true;

  constructor() {
    TypingLines.#live.add(this);
  }

  // Offline no re-pulse can arrive: expiry pauses, reconnect re-arms a fresh TTL (S129).
  static setConnected(connected: boolean): void {
    if (connected === TypingLines.#connected) return;
    TypingLines.#connected = connected;
    for (const lines of TypingLines.#live) {
      for (const [fromId, turns] of lines.#turns) {
        for (const [key, turn] of turns) lines.#arm(fromId, key, turn);
      }
    }
  }

  add(event: TypingEvent): void {
    const turns = this.#turns.get(event.fromId) ?? new Map<string, LiveTurn>();
    this.#turns.set(event.fromId, turns);
    const key = event.turnId ?? UNTAGGED;
    clearTimeout(turns.get(key)?.timer);
    const turn: LiveTurn = { parentId: event.parentId };
    this.#arm(event.fromId, key, turn);
    turns.set(key, turn);
    const idx = this.typing.findIndex((t) => t.fromId === event.fromId);
    if (idx < 0) {
      this.typing = [...this.typing, event];
    } else if (this.typing[idx].activity !== event.activity) {
      const next = this.typing.slice();
      next[idx] = event;
      this.typing = next;
    }
  }

  // A cybo's stop frame. One without a turnId cannot name its turn, so it ends the line.
  end(event: TypingEvent): void {
    if (event.turnId) this.#endTurn(event.fromId, event.turnId);
    else this.clearFor(event.fromId);
  }

  // Ends the sender's live turns answering under `parentId`; true when there was one.
  endAt(fromId: string, parentId: string): boolean {
    const keys = [...(this.#turns.get(fromId) ?? [])]
      .filter(([, turn]) => turn.parentId === parentId)
      .map(([key]) => key);
    for (const key of keys) this.#endTurn(fromId, key);
    return keys.length > 0;
  }

  // The sender's message landed: every turn of theirs is answered.
  clearFor(fromId: string): void {
    const turns = this.#turns.get(fromId);
    if (!turns) return;
    for (const turn of turns.values()) clearTimeout(turn.timer);
    this.#turns.delete(fromId);
    this.typing = this.typing.filter((t) => t.fromId !== fromId);
  }

  clear(): void {
    for (const turns of this.#turns.values()) {
      for (const turn of turns.values()) clearTimeout(turn.timer);
    }
    this.#turns.clear();
    if (this.typing.length > 0) this.typing = [];
  }

  #arm(fromId: string, key: string, turn: LiveTurn): void {
    clearTimeout(turn.timer);
    turn.timer = TypingLines.#connected
      ? setTimeout(() => this.#endTurn(fromId, key), TYPING_EXPIRY_MS)
      : undefined;
  }

  #endTurn(fromId: string, key: string): void {
    const turns = this.#turns.get(fromId);
    const turn = turns?.get(key);
    if (!turns || !turn) return;
    clearTimeout(turn.timer);
    turns.delete(key);
    if (turns.size > 0) return;
    this.#turns.delete(fromId);
    this.typing = this.typing.filter((t) => t.fromId !== fromId);
  }
}
